import { CommonModule } from '@angular/common';
import {
  Component,
  HostBinding,
  OnDestroy,
  TemplateRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AbbTableComponent,
  DataTableFilter,
  FilterOption,
  IColumn,
} from '@components/abb-table/abb-table.component';
import { AbbDateComponent } from '@components/abb-date/abb-date.component';
import { Apollo } from 'apollo-angular';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import {
  AddToReportHistoryMutation,
  AddToReportHistoryMutationVariables,
  AutomationsLogsExportQuery,
  AutomationsLogsExportQueryVariables,
  AutomationsLogsQuery,
  AutomationsLogsQueryVariables,
  DeviceSelectOptionsQuery,
  DeviceSelectOptionsQueryVariables,
  GroupSelectOptionsQuery,
  GroupSelectOptionsQueryVariables,
  OutcomeIndicator,
  PropertySelectOptionsQuery,
  PropertySelectOptionsQueryVariables,
  Report_Category,
  Status,
} from 'gql/graphql';
import { finalize, first, map, Subscription, tap } from 'rxjs';
import { RouterModule } from '@angular/router';
import { CONTROLS_SUBTITLE } from '@pages/controls/controls';
import {
  automationsLogsExportQuery,
  automationsLogsQuery,
} from '@graphql/automations';
import { DataProvider } from '@components/abb-table/data-source';
import { AbbOutcomeIndicatorComponent } from '@components/abb-outcome-indicator/abb-outcome-indicator.component';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/ui/toast.service';
import { downloadBase64File } from '@utils/download';
import { addToReportHistoryMutation, reportListQuery } from '@graphql/report';
import { replaceTemplatePlaceholders, t } from '@utils/strings';
import { jsonParsePipe } from '@pipes/jsonParse.pipe';
import { deviceSelectOptionsQuery } from '@graphql/asset-management';
import { groupSelectOptionsQuery } from '@graphql/group';
import { propertySelectOptionsQuery } from '@graphql/property';
import { getErrorMessage } from '@utils/errors';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { MatTooltipModule } from '@angular/material/tooltip';

type AutomationsLogsDataTableFilter = {
  date: {
    start: { date: Date | null; time: boolean | null };
    end: { date: Date | null; time: boolean | null };
  };
  groupId: string | null;
  deviceId: string | null;
  propertyId: string | null;
};

@Component({
  selector: 'abb-logs',
  standalone: true,
  imports: [
    AbbTableComponent,
    CommonModule,
    TPipe,
    FormsModule,
    AbbDateComponent,
    RouterModule,
    AbbOutcomeIndicatorComponent,
    jsonParsePipe,
    OverflowTooltipDirective,
    MatTooltipModule,
  ],
  template: `
    <abb-table
      [columns]="columns()"
      [dataProvider]="dataProvider"
      [filters]="filters()"
      [defaultFilters]="defaultFilters"
      [loading]="loading()"
      [dataExport]="true"
      (onExport)="onExport($event)"
      [exporting]="exporting()"
    ></abb-table>
    <ng-template #alarm let-item>
      {{
        (item.alarm.status === Status.Ok || item.alarm.status === Status.Warning
          ? 'No'
          : 'Yes'
        ) | t
      }}
    </ng-template>
    <ng-template #properties let-item>
      <span overflowTooltip class="block truncate">
        @for (p of item.properties; track p.name) {
        {{ p.name }}@if (!$last) {, } }
      </span>
    </ng-template>
    <ng-template #devices let-item>
      <span overflowTooltip class="block truncate">
        @for (d of item.devices; track d.name) {
        {{ d.name }}@if (!$last) {, } }
      </span>
    </ng-template>
    <ng-template #groups let-item>
      <span overflowTooltip class="block truncate">
        @for (g of item.groups; track g.name) {
        {{ g.name }}@if (!$last) {, } }
      </span>
    </ng-template>
    <ng-template #if let-item>
      <span overflowTooltip class="block w-full truncate">
        {{
          (item.if.conditions?.[0].deviceCategory?.name | t) ||
            ((item.groups?.length > 0 ? 'Group' : 'Property') | t)
        }}
        {{ (item.if.conditions?.[0].eventType.name | t).toLowerCase() }}
        {{ item.if.conditions?.[0]?.threshold+(item.if.conditions?.[0]?.measure?.name | t) }}
      </span>
    </ng-template>
    <ng-template #then let-item>
      @if(item.then.action.nameWithPlaceholder) {
      <span
        class="block truncate"
        overflowTooltip
        *ngIf="replaceTemplatePlaceholders(item.then.action.nameWithPlaceholder, {
            device: '&lt;a href=&quot;/asset-management/devices/'+item.then.device.id+'&quot; class=&quot;text-abb-primary/80 font-medium hover:text-abb-primary transition-colors cursor-pointer&quot;&gt;'+item.then.device.name+'&lt;/a&gt;',
          }, true) | jsonParse as parsedHtml"
        [innerHTML]="parsedHtml.value"
      >
      </span>
      } @else {
      <span class="block truncate" overflowTooltip>
        {{ item.then.action.name }}
      </span>
      }
    </ng-template>
    <ng-template #when let-item>
      <div class="flex gap-2 items-center">
        <span overflowTooltip class="truncate">
          <abb-date
            [date]="item.when.interval.fromDate"
            [showTime]="true"
            [showTooltip]="false"
          ></abb-date
          >-<abb-date
            [date]="item.when.interval.toDate"
            [showTime]="true"
            [showTooltip]="false"
          ></abb-date>
        </span>
      </div>
    </ng-template>
    <ng-template #automationTime let-item>
      @if (item.automationTime === null) {
      {{ '-' }}
      } @else {
      <abb-date
        [date]="item.automationTime"
        [showTime]="true"
        [showTooltip]="true"
      ></abb-date>
      }
    </ng-template>
    <ng-template #status let-item>
      <div class="flex gap-2 items-center justify-center">
        <abb-outcome-indicator
          class="inline-flex"
          variant="outlined"
          size="small"
          [indicator]="item.status"
          [matTooltip]="
            (item.status === OutcomeIndicator.Ok ? 'Successfully' : 'Failure')
              | t
          "
        ></abb-outcome-indicator>
        <!--  <span class="truncate">
          {{
            (item.status === OutcomeIndicator.Ok ? 'Successfully' : 'Failure')
              | t
          }}</span
        > -->
      </div>
    </ng-template>
  `,
  styles: ``,
  host: {
    class: 'mt-2',
  },
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'controls.tabs.automations.tabs.logs',
    },
  ],
})
export class LogsComponent implements OnDestroy {
  @HostBinding('attr.id') componentid = 'automation-log-component';

  readonly replaceTemplatePlaceholders = replaceTemplatePlaceholders;
  readonly Status = Status;
  readonly OutcomeIndicator = OutcomeIndicator;
  readonly ReportCategory = Report_Category;

  subtitle = inject(CONTROLS_SUBTITLE);
  prefix = inject(TRANSLATION_PREFIX);
  loading = signal(true);
  exporting = signal(false);
  lang = signal('');

  whenTemplate = viewChild<TemplateRef<unknown>>('when');
  ifTemplate = viewChild<TemplateRef<unknown>>('if');
  thenTemplate = viewChild<TemplateRef<unknown>>('then');
  automationTimeTemplate = viewChild<TemplateRef<unknown>>('automationTime');
  alarmTemplate = viewChild<TemplateRef<unknown>>('alarm');
  propertiesTemplate = viewChild<TemplateRef<unknown>>('properties');
  groupsTemplate = viewChild<TemplateRef<unknown>>('groups');
  devicesTemplate = viewChild<TemplateRef<unknown>>('devices');
  statusTemplate = viewChild<TemplateRef<unknown>>('status');

  deviceSelectOptions = signal<FilterOption[]>([]);
  propertySelectOptions = signal<FilterOption[]>([]);
  groupSelectOptions = signal<FilterOption[]>([]);

  dataProvider: DataProvider<
    AutomationsLogsQuery['automationsLogs']['items'][number],
    AutomationsLogsDataTableFilter
  > = ({ range, filters }) => {
    return this.apollo
      .query<AutomationsLogsQuery, AutomationsLogsQueryVariables>({
        query: automationsLogsQuery,
        variables: {
          from: range.start,
          to: range.end,
          fromDate: filters.date.start.date,
          toDate: filters.date.end.date,
          device: filters.deviceId,
          property: filters.propertyId,
          group: filters.groupId,
        },
      })
      .pipe(
        map((res) => res.data.automationsLogs),
        finalize(() => this.loading.set(false))
      );
  };

  columns = computed<
    IColumn<AutomationsLogsQuery['automationsLogs']['items'][number]>[]
  >(() => {
    return [
      {
        id: 'name',
        name: 'Name',
        key: 'name',
        translate: true,
      },
      {
        id: 'when',
        name: 'When',
        templateRef: this.whenTemplate(),
        translate: true,
      },
      {
        id: 'if',
        name: 'If',
        templateRef: this.ifTemplate(),
        translate: true,
      },
      {
        id: 'then',
        name: 'Then',
        templateRef: this.thenTemplate(),
        translate: true,
      },
      {
        id: 'automationTime',
        name: 'Automation time',
        translate: true,
        templateRef: this.automationTimeTemplate(),
      },
      // {
      //   id: 'alarm',
      //   name: 'Alarm',
      //   templateRef: this.alarmTemplate(),
      //   translate: true,
      // },
      {
        id: 'devices',
        name: 'Devices',
        templateRef: this.devicesTemplate(),
        translate: true,
      },
      {
        id: 'status',
        name: 'Status',
        templateRef: this.statusTemplate(),
        translate: true,
        tableHeaderCellClass: '[&>*]:!justify-center',
      },
    ];
  });

  filters = computed<DataTableFilter<AutomationsLogsDataTableFilter>[]>(() => {
    return [
      {
        id: 'date',
        filterType: 'datetimeRange',
        className: 'w-full',
        label: 'Date',
      },
      {
        id: 'deviceId',
        filterOptions: this.deviceSelectOptions(),
        filterType: 'select',
        className: 'w-full',
        label: 'Device',
      },
      // {
      //   id: 'propertyId',
      //   filterOptions: this.propertySelectOptions(),
      //   filterType: 'select',
      //   className: 'w-full',
      //   label: 'Property',
      // },
      // {
      //   id: 'groupId',
      //   filterOptions: this.groupSelectOptions(),
      //   filterType: 'select',
      //   className: 'w-full',
      //   label: 'Group',
      // },
    ];
  });
  defaultFilters: AutomationsLogsDataTableFilter = {
    date: {
      start: {
        date: null,
        time: null,
      },
      end: {
        date: null,
        time: null,
      },
    },
    groupId: null,
    deviceId: null,
    propertyId: null,
  };

  private subscriptions: Subscription[] = [];
  constructor(
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly toast: ToastService
  ) {
    this.lang.set(translate.currentLang);
    this.subscriptions.push(
      apollo
        .watchQuery<
          DeviceSelectOptionsQuery,
          DeviceSelectOptionsQueryVariables
        >({ query: deviceSelectOptionsQuery })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.deviceSelectOptions.set(
              data.deviceList.map((device) => ({
                value: device.id,
                label: device.name,
                skipTranslation: true,
              }))
            );
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),
      apollo
        .watchQuery<
          PropertySelectOptionsQuery,
          PropertySelectOptionsQueryVariables
        >({ query: propertySelectOptionsQuery })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.propertySelectOptions.set(
              data.propertyList.map((property) => ({
                value: property.id,
                label: property.name,
                skipTranslation: true,
              }))
            );
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),
      apollo
        .watchQuery<GroupSelectOptionsQuery, GroupSelectOptionsQueryVariables>({
          query: groupSelectOptionsQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.groupSelectOptions.set(
              data.groupList.map((group) => ({
                value: group.id,
                label: group.name,
                skipTranslation: true,
              }))
            );
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onExport(filters: any) {
    this.exporting.set(true);
    const fromDate = filters.date.start.date;
    const toDate = filters.date.end.date;
    this.subscriptions.push(
      this.apollo
        .query<AutomationsLogsExportQuery, AutomationsLogsExportQueryVariables>(
          {
            query: automationsLogsExportQuery,
            variables: {
              fromTs: fromDate ?? null,
              toTs: toDate ?? null,
              device: filters.deviceId ?? null,
              property: filters.propertyId ?? null,
              group: filters.groupId ?? null,
            },
          }
        )
        .pipe(first())
        .subscribe({
          next: ({ data }) => {
            downloadBase64File(data.automationsLogsExport);
            this.apollo
              .mutate<
                AddToReportHistoryMutation,
                AddToReportHistoryMutationVariables
              >({
                mutation: addToReportHistoryMutation,
                variables: {
                  id: data.automationsLogsExport.id,
                  filename: data.automationsLogsExport.filename,
                  type: Report_Category.Historical,
                },
                update: (cache, { data }) => {
                  cache.writeQuery({
                    query: reportListQuery,
                    data: { reportList: data?.addToReportHistory },
                  });
                },
              })
              .pipe(first())
              .subscribe();
            this.exporting.set(false);
            this.toast.notifySuccess(
              t(this.prefix, `File exported successfully`, this.translate)
            );
          },
          error: () => {
            this.exporting.set(false);
            this.toast.notifyError(
              t(
                this.prefix,
                `An error occurred while exporting file`,
                this.translate
              )
            );
          },
        })
    );
  }
}
